import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bike, Save } from "lucide-react";
import { supabase, rpc } from "@/lib/supabase";
import { PageHeader, Card, Loader } from "@/components/kit";
import { VEHICLE_LABELS } from "@/lib/format";

const field =
  "w-full rounded-xl border border-[#27272A] bg-[#0d0d0f] px-3 py-2 text-sm text-white outline-none focus:border-[#FF6A00]";
const labelCls = "mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500";

function RuleCard({ rule, onSaved }) {
  const [r, setR] = useState(rule);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setR({ ...r, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      const driverShare = Math.min(1, Math.max(0, Number(r.driver_share)));
      await rpc("admin_update_pricing", {
        p_vehicle_type: r.vehicle_type,
        p_base_fare: Number(r.base_fare),
        p_included_km: Number(r.included_km),
        p_price_per_extra_km: Number(r.price_per_extra_km),
        p_min_fare: Number(r.min_fare),
        p_driver_share: driverShare,
        p_platform_share: Number((1 - driverShare).toFixed(3)),
        p_offer_timeout_seconds: Number(r.offer_timeout_seconds),
        p_is_active: !!r.is_active,
        p_max_packages: Number(r.max_packages),
        p_max_weight_kg: Number(r.max_weight_kg),
        p_max_length_cm: Number(r.max_length_cm),
        p_max_width_cm: Number(r.max_width_cm),
        p_max_height_cm: Number(r.max_height_cm),
        p_heavy_weight_kg: Number(r.heavy_weight_kg),
      });
      toast.success(`Tarifa de ${VEHICLE_LABELS[r.vehicle_type]} guardada`);
      onSaved();
    } catch (e) {
      toast.error(e?.message || "Error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid={`pricing-card-${r.vehicle_type}`}>
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2 font-heading text-base font-bold text-white">
          <Bike size={18} className="text-[#FF6A00]" /> {VEHICLE_LABELS[r.vehicle_type]}
        </div>
        <label className="flex cursor-pointer items-center gap-2 text-xs font-bold">
          <input type="checkbox" checked={!!r.is_active} onChange={(e) => setR({ ...r, is_active: e.target.checked })} data-testid={`pricing-active-${r.vehicle_type}`} />
          <span className={r.is_active ? "text-emerald-400" : "text-gray-500"}>{r.is_active ? "Activo" : "Inactivo"}</span>
        </label>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <div><label className={labelCls}>Tarifa base (COP)</label><input type="number" className={field} value={r.base_fare} onChange={set("base_fare")} /></div>
        <div><label className={labelCls}>Km incluidos</label><input type="number" step="0.1" className={field} value={r.included_km} onChange={set("included_km")} /></div>
        <div><label className={labelCls}>Precio km extra</label><input type="number" className={field} value={r.price_per_extra_km} onChange={set("price_per_extra_km")} /></div>
        <div><label className={labelCls}>Tarifa mínima</label><input type="number" className={field} value={r.min_fare} onChange={set("min_fare")} /></div>
        <div><label className={labelCls}>% Conductor (0-1)</label><input type="number" step="0.01" min="0" max="1" className={field} value={r.driver_share} onChange={set("driver_share")} data-testid={`pricing-driver-share-${r.vehicle_type}`} /></div>
        <div><label className={labelCls}>Oferta (seg)</label><input type="number" className={field} value={r.offer_timeout_seconds} onChange={set("offer_timeout_seconds")} /></div>
      </div>
      <div className="mt-4 border-t border-[#27272A] pt-4">
        <div className="mb-2 text-[11px] font-bold uppercase tracking-wider text-gray-500">Capacidad de carga (límites del piloto)</div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <div><label className={labelCls}>Máx. paquetes</label><input type="number" step="1" min="1" className={field} value={r.max_packages ?? ""} onChange={set("max_packages")} data-testid={`cap-max-packages-${r.vehicle_type}`} /></div>
          <div><label className={labelCls}>Máx. peso (kg)</label><input type="number" step="0.1" min="0" className={field} value={r.max_weight_kg ?? ""} onChange={set("max_weight_kg")} data-testid={`cap-max-weight-${r.vehicle_type}`} /></div>
          <div><label className={labelCls}>Umbral pesada (kg)</label><input type="number" step="0.1" min="0" className={field} value={r.heavy_weight_kg ?? ""} onChange={set("heavy_weight_kg")} data-testid={`cap-heavy-${r.vehicle_type}`} /></div>
          <div><label className={labelCls}>Máx. largo (cm)</label><input type="number" step="0.1" min="0" className={field} value={r.max_length_cm ?? ""} onChange={set("max_length_cm")} data-testid={`cap-max-length-${r.vehicle_type}`} /></div>
          <div><label className={labelCls}>Máx. ancho (cm)</label><input type="number" step="0.1" min="0" className={field} value={r.max_width_cm ?? ""} onChange={set("max_width_cm")} data-testid={`cap-max-width-${r.vehicle_type}`} /></div>
          <div><label className={labelCls}>Máx. alto (cm)</label><input type="number" step="0.1" min="0" className={field} value={r.max_height_cm ?? ""} onChange={set("max_height_cm")} data-testid={`cap-max-height-${r.vehicle_type}`} /></div>
        </div>
      </div>
      <button onClick={save} disabled={saving} data-testid={`pricing-save-${r.vehicle_type}`} className="mt-4 flex items-center gap-2 rounded-xl bg-[#FF6A00] px-5 py-2.5 text-sm font-extrabold text-black transition-transform active:scale-95 disabled:opacity-60">
        <Save size={15} /> {saving ? "Guardando…" : "Guardar"}
      </button>
    </Card>
  );
}

export default function AdminSettings() {
  const [rules, setRules] = useState(null);
  const load = () =>
    supabase.from("pricing_rules").select("*").order("vehicle_type").then(({ data }) => setRules(data || []));
  useEffect(() => { load(); }, []);
  if (rules === null) return <Loader />;
  return (
    <div>
      <PageHeader title="Configuración de tarifas" subtitle="Ajusta precios, comisión y tiempo de oferta por vehículo." testId="admin-settings" />
      <div className="grid gap-4">
        {rules.map((r) => (
          <RuleCard key={r.id} rule={r} onSaved={load} />
        ))}
      </div>
    </div>
  );
}
